import type { EndlessBestRecord } from "./types";

const STORAGE_KEY = "snake-arena-endless-best-v1";

export interface EndlessStorageState {
  schemaVersion: 1;
  /** `null` means "no record yet" - never a fabricated zero-value record, so the very first valid terminal run (even a Score of exactly 0) is unambiguously a new best. */
  best: EndlessBestRecord | null;
}

function defaultState(): EndlessStorageState {
  return { schemaVersion: 1, best: null };
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isValidRecord(value: unknown): value is EndlessBestRecord {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<EndlessBestRecord>;
  return isFiniteNumber(candidate.length) && isFiniteNumber(candidate.score) && isFiniteNumber(candidate.survivalTimeMs);
}

function isValid(value: unknown): value is EndlessStorageState {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<EndlessStorageState>;
  if (candidate.schemaVersion !== 1) return false;
  if (candidate.best === null) return true;
  return isValidRecord(candidate.best);
}

/**
 * The locked comparator: 1) higher Length always wins. 2) equal Length ->
 * higher Score wins (an equal-Length run can still improve the record
 * through a better Score - see `ENDLESS_HEADLINE_BY_REASON`'s sibling
 * "NEW BEST" copy, deliberately not "NEW BEST LENGTH"). 3) survival time
 * is NEVER a tie-break, whatever it is. 4) an exact tie (Length AND Score
 * both equal) never replaces the record - the earlier stored run is kept.
 * `current === null` ("no record yet") always loses to any candidate, so
 * the very first valid terminal run always becomes the record, whatever
 * its Score.
 */
function isBetter(candidate: EndlessBestRecord, current: EndlessBestRecord | null): boolean {
  if (!current) return true;
  if (candidate.length !== current.length) return candidate.length > current.length;
  if (candidate.score !== current.score) return candidate.score > current.score;
  return false;
}

/**
 * Endless Growth V1's fully local, personal-best storage - its own
 * unique `localStorage` key, entirely independent of Campaign's
 * `ProgressStorage`/`ScoreV2Storage` and Daily's storage keys (see
 * `GameConfig.storage`'s doc for that separation precedent). Stores
 * exactly ONE canonical best-run record from ONE real run - never a
 * Frankenstein combination of the best Length from one run, the best
 * Score from another, and the best time from a third. Callers decide
 * WHICH runs are eligible to call `recordRun()` at all (death, quit, and
 * arena-cleared - see `EndlessEndReason`; never an unfinished run
 * interrupted by a refresh/tab close, which never reaches a terminal
 * result in the first place) - this module itself is reason-agnostic, it
 * only ever applies the locked comparator to whatever candidate it's given.
 */
export const EndlessStorage = {
  get(): EndlessStorageState {
    try {
      const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      const parsed = JSON.parse(raw);
      return isValid(parsed) ? parsed : defaultState();
    } catch {
      return defaultState();
    }
  },

  save(state: EndlessStorageState): void {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage failures - the game still works, just without persistence.
    }
  },

  getBest(): EndlessBestRecord | null {
    return this.get().best;
  },

  /** Whether a real best record has ever been stored - lets the HUD/result screen distinguish "no record yet" from a genuine stored record whose Score happens to be 0. */
  hasBest(): boolean {
    return this.get().best !== null;
  },

  /**
   * Records one real, terminal run's canonical result - only ever
   * replaces the stored record when `isBetter()` (the locked comparator)
   * says the candidate wins; the record can never decrease. Returns
   * whether this run actually became the new best.
   */
  recordRun(candidate: EndlessBestRecord): { isNewBest: boolean } {
    const state = this.get();
    const isNewBest = isBetter(candidate, state.best);
    if (isNewBest) {
      this.save({ ...state, best: candidate });
    }
    return { isNewBest };
  },

  /** QA-only: clears the local best record entirely, back to "no record yet". Never called by real gameplay. */
  clear(): void {
    this.save(defaultState());
  },
};
